import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

interface LinkDialogProps {
  showDialog: boolean;
  onOpenChange: (open: boolean) => void;
  linkUrl: string;
  onLinkUrlChange: (url: string) => void;
  /**
   * Called when the user confirms. The third argument indicates whether the
   * URL should be considered as additional context for AI responses in this
   * note. Defaults to false so users opt-in per link.
   */
  onAddLink: (url: string, displayText?: string, useAsAIContext?: boolean) => void;
  /** Text currently selected in the editor, used as default display text. */
  selectedText?: string;
}

const LinkDialog: React.FC<LinkDialogProps> = ({
  showDialog,
  onOpenChange,
  linkUrl,
  onLinkUrlChange,
  onAddLink,
  selectedText,
}) => {
  const [displayText, setDisplayText] = useState("");
  const [useAsAIContext, setUseAsAIContext] = useState(false);

  // Pre-fill display text with the editor selection each time the dialog opens.
  useEffect(() => {
    if (showDialog) {
      setDisplayText(selectedText?.trim() ?? "");
      setUseAsAIContext(false);
    }
  }, [showDialog, selectedText]);

  const handleSubmit = () => {
    if (!linkUrl) return;
    const label = displayText.trim();
    onAddLink(linkUrl, label.length > 0 ? label : undefined, useAsAIContext);
    setDisplayText("");
    setUseAsAIContext(false);
  };

  return (
    <Dialog open={showDialog} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add link</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="link-url">URL</Label>
            <Input
              id="link-url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => onLinkUrlChange(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="link-text">Display text (optional)</Label>
            <Input
              id="link-text"
              placeholder={selectedText ? "Selected text will be hyperlinked" : "Leave empty to show the URL"}
              value={displayText}
              onChange={(e) => setDisplayText(e.target.value)}
            />
            {selectedText && (
              <p className="text-xs text-muted-foreground">
                Your selection will be replaced with this hyperlink.
              </p>
            )}
          </div>
          <div className="flex items-start gap-2 rounded-md border border-border p-3">
            <Checkbox
              id="link-ai-context"
              checked={useAsAIContext}
              onCheckedChange={(checked) => setUseAsAIContext(checked === true)}
              className="mt-0.5"
            />
            <div className="space-y-1">
              <Label htmlFor="link-ai-context" className="cursor-pointer">
                Use as AI context
              </Label>
              <p className="text-xs text-muted-foreground">
                When enabled, the AI will read this link's content and consider it when answering in this note.
              </p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={!linkUrl}>
            Add link
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkDialog;
